import invariant from 'invariant';
import {
  GQBiBaseFieldMeta,
  GQBiDimensionFieldMeta,
  GQBiEmptyStringType,
  GQBiFieldMeta,
  GQBiFieldNumberMeta,
  GQBiFieldStringMeta,
  GQBiKnownDisplayValueMappingType,
  GQBiMeasureFieldMeta,
  GQBiQueryComponentsInput,
  GQBiQueryMode,
  GQBiQueryOrderDirection,
  GQBiSavedView,
  GQBiSavedViewQueryContextInput,
  GQBiSavedViewVisibility,
  GQBiChartKind,
  GQBiAggregateMethod,
  GQBiQueryMetadataResult,
  GQFlags,
} from '../generated/graphql';
import { convertAllNullsToUndefined } from '../utils/convertNullToUndefined';

import * as z from 'zod';
import { StripPrefix, StripSuffix } from '../utils/utilTypes';
import { YMInterval } from '../utils/YearMonth';
import { ymIntervalSchema } from '../zodSchemas/utilSchemas';
import omit from 'lodash/omit';
import assertNever from '../utils/assertNever';
import {
  NumericValueWithMetadata,
  addMetaToDynamicNormalizedMeasure,
  findMeasureMeta,
} from './utils';
import { SavedViewTemplateIntervalType } from './savedViewTemplates';

export const BiFieldTypeSchema = z.enum([
  'string',
  'number',
  'boolean',
  'date',
]);
export const BiFieldType = BiFieldTypeSchema.enum;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type BiFieldType = z.infer<typeof BiFieldTypeSchema>;

// Fancy distribution over a union. The condition catcches a case wher boolean is actually a union of true and false
// and also serves the purpose of causing the distribution (conditionals automatically distribute over the union in TS)
type ArrayifyUnion<T> = T extends true | false ? Array<boolean> : Array<T>;

export interface BiFieldTypeToPrimitiveType {
  [BiFieldType.string]: string;
  [BiFieldType.number]: number;
  [BiFieldType.boolean]: boolean;
  [BiFieldType.date]: number;
}
export type BiRawSingularType =
  | BiFieldTypeToPrimitiveType[keyof BiFieldTypeToPrimitiveType]
  | null
  | undefined;

export type BiRawArrayType = ArrayifyUnion<
  BiFieldTypeToPrimitiveType[keyof BiFieldTypeToPrimitiveType]
>;

export type BiRawValueType = BiRawSingularType | BiRawArrayType;

export type BiFieldMetaToPrimitiveType<M extends BiFieldMeta> =
  | (true | false | undefined extends M['isMultiValue']
      ?
          | ArrayifyUnion<BiFieldTypeToPrimitiveType[M['type']]>
          | BiFieldTypeToPrimitiveType[M['type']]
      : M['isMultiValue'] extends true
        ? ArrayifyUnion<BiFieldTypeToPrimitiveType[M['type']]>
        : M['isMultiValue'] extends false
          ? BiFieldTypeToPrimitiveType[M['type']]
          : undefined extends M['isMultiValue']
            ? BiFieldTypeToPrimitiveType[M['type']]
            : never)
  | null
  | undefined;

export const biCommonFieldMetaSchema = z.object({
  fieldId: z.string(),
  displayName: z.string(),
  // TODO: put this in gql
  description: z.string().optional(),
  // controls the grouping of options in the select dropdown
  fieldFamily: z.string().optional(),
  // naming?
  hiddenInFilters: z.boolean().optional(),
  hiddenInGroupBy: z.boolean().optional(),
  // default is Empty
  emptyStringType: z.nativeEnum(GQBiEmptyStringType).optional(),
  isMultiValue: z.boolean().optional(),
  // If undefined, use fieldId for the marta field name.
  martaFieldName: z.string().optional(),
  // If undefined, default to false
  isCustomField: z.boolean().optional(),
  // If undefined, viewable by everyone
  gatingFeatureFlag: z.nativeEnum(GQFlags).optional(),
});
export type BiCommonFieldMeta = z.infer<typeof biCommonFieldMetaSchema>;

const baseListOptionItemSchema = z.object({
  label: z.string().nullable(),
});
export const dimensionListOptionItemSchema = baseListOptionItemSchema.extend({
  value: z.union([z.string(), z.boolean()]).nullable(),
});
export const stringDimensionListOptionItemSchema =
  baseListOptionItemSchema.extend({
    value: z.string().nullable(),
  });

export const biStringFieldMetaSchema = biCommonFieldMetaSchema.merge(
  z.object({
    type: z.literal(BiFieldType.string),
    shouldSentenceCaseValues: z.boolean().optional(),
    shouldNullifyPseudoNulls: z.boolean().optional(),
    knownDisplayValueMappingType: z
      .nativeEnum(GQBiKnownDisplayValueMappingType)
      .optional(),
    staticListOptions: z.array(stringDimensionListOptionItemSchema).optional(),
  })
);
export type BiStringFieldMeta = z.infer<typeof biStringFieldMetaSchema>;

/**
 * Format of BI timeseries data. We are keeping this in shared-universal because
 * anomaly detection needs it.
 */
export type FormattedTimeSeriesDataRow = {
  measure: NumericValueWithMetadata;
  timeRaw: BiRawValueType;
  groupId: string;
  groupFormatted: string;
};

/**
 * - sum:
 * Suitable for summable quantities like kg of carbon.
 * Results in sum() sql aggregate function.
 *
 * - avg:
 * Suitable for quantities that must be averaged regardless of the
 * grouping dimensions e.g. employee salary, hourly rate.
 * Results in avg() sql aggregate function.
 *
 * - monthlyAvg:
 * Suitable for "gauges" like employee headcount or building sqft.
 * If 'month' is one of the grouping dimensions, behaves like 'sum'.
 * If 'month' is not one of the dimensions, we will first aggregate
 * with a 'sum' function across all grouping dimensions plus 'month',
 * then we aggregate the resulting summed quantity with an 'avg'
 * function across all grouping dimensions but not month. E.g.
 *
 * with inner as (
 *   select month, country, sum(headcount) as headcount
 *   from employee_data group by month, country
 * )
 * select country, avg(headcout) as headcount
 * from inner group by country
 */
export type AggregateMethod = GQBiAggregateMethod;

export const biNumberFieldMetaSchema = biCommonFieldMetaSchema.merge(
  z.object({
    type: z.literal(BiFieldType.number),
    format: z.string().optional(),
    aggregateType: z.nativeEnum(GQBiAggregateMethod).optional(),

    precision: z.number().optional(),
    disableCommas: z.boolean().optional(),
    includeUnit: z.boolean().optional(),
    shouldFormatToPercent: z.boolean().optional(),

    unit: z.string().optional(),
    unitDimension: z.string().optional(),
    dependentDimensions: z.array(z.string()).optional(),
    aggregatedUnitDimension: z.string().optional(),

    // Optional scaling factor by which underlying data should be divided before being returned.
    divideBy: z.number().optional(),
    unitUserFacing: z.string().optional(),
  })
);
export type BiNumberFieldMeta = z.infer<typeof biNumberFieldMetaSchema>;

export const biBooleanFieldMetaSchema = biCommonFieldMetaSchema.merge(
  z.object({
    type: z.literal(BiFieldType.boolean),
  })
);
export type BiBooleanFieldMeta = z.infer<typeof biBooleanFieldMetaSchema>;

export const biDateFieldMetaSchema = biCommonFieldMetaSchema.merge(
  z.object({
    type: z.literal(BiFieldType.date),
    format: z.string().optional(),
  })
);
export type BiDateFieldMeta = z.infer<typeof biDateFieldMetaSchema>;

export const biFieldMeta = z.discriminatedUnion('type', [
  biStringFieldMetaSchema,
  biNumberFieldMetaSchema,
  biBooleanFieldMetaSchema,
  biDateFieldMetaSchema,
]);
export type BiFieldMeta = z.infer<typeof biFieldMeta>;

export const biDimensionFieldMetaSchema = biFieldMeta;
export type BiDimensionFieldMeta = z.infer<typeof biDimensionFieldMetaSchema>;

export const biMeasureFieldMetaSchema = biNumberFieldMetaSchema;
export type BiMeasureFieldMeta = z.infer<typeof biMeasureFieldMetaSchema>;

export function transformGqlFieldMetaToStandardMeta<M extends GQBiFieldMeta>({
  __typename,
  ...gqlMeta
}: GQBiFieldMeta): Extract<
  M,
  {
    type: StripSuffix<
      StripPrefix<NonNullable<typeof __typename>, 'BiField'>,
      'Meta'
    >;
  }
> {
  invariant(__typename, 'Expected __typename to be defined');
  return {
    ...convertAllNullsToUndefined(gqlMeta),
    type: __typename
      .replace('BiField', '')
      .replace('Meta', '')
      .toLowerCase() as BiFieldType,
  } satisfies BiFieldMeta as any;
}

function transformFieldMetaToBaseGqFieldMeta(
  fieldMeta: BiFieldMeta
): GQBiBaseFieldMeta {
  return {
    fieldId: fieldMeta.fieldId,
    displayName: fieldMeta.displayName,
    fieldFamily: fieldMeta.fieldFamily ?? null,
    description: fieldMeta.description ?? null,
    hiddenInFilters: fieldMeta.hiddenInFilters ?? null,
    hiddenInGroupBy: fieldMeta.hiddenInGroupBy ?? null,
    emptyStringType: fieldMeta.emptyStringType ?? null,
    isMultiValue: fieldMeta.isMultiValue ?? null,
    isCustomField: fieldMeta.isCustomField ?? null,
  };
}

export function transformStandardMetaToGqlFieldMeta(
  fieldMeta: BiFieldMeta
): GQBiFieldMeta {
  switch (fieldMeta.type) {
    case BiFieldType.string:
      return transformBiStringFieldMetaToGq(fieldMeta);
    case BiFieldType.number:
      return transformBiNumberFieldMetaToGq(fieldMeta);
    case BiFieldType.boolean:
      return {
        __typename: 'BiFieldBooleanMeta',
        ...transformFieldMetaToBaseGqFieldMeta(fieldMeta),
      } satisfies GQBiFieldMeta;
    case BiFieldType.date:
      return {
        __typename: 'BiFieldDateMeta',
        ...transformFieldMetaToBaseGqFieldMeta(fieldMeta),
      } satisfies GQBiFieldMeta;
  }
}

export function transformBiDimensionFieldMetaToGq(
  fieldMeta: BiDimensionFieldMeta
): GQBiDimensionFieldMeta {
  return transformStandardMetaToGqlFieldMeta(fieldMeta);
}

export function transformBiMeasureFieldMetaToGq(
  fieldMeta: BiMeasureFieldMeta
): GQBiMeasureFieldMeta {
  return transformBiNumberFieldMetaToGq(fieldMeta);
}

export function transformBiStringFieldMetaToGq(
  fieldMeta: BiStringFieldMeta
): GQBiFieldStringMeta {
  return {
    __typename: 'BiFieldStringMeta',
    ...transformFieldMetaToBaseGqFieldMeta(fieldMeta),
    shouldSentenceCaseValues: fieldMeta.shouldSentenceCaseValues ?? false,
    shouldNullifyPseudoNulls: fieldMeta.shouldNullifyPseudoNulls ?? null,
    knownDisplayValueMappingType:
      fieldMeta.knownDisplayValueMappingType ?? null,
    staticListOptions: fieldMeta.staticListOptions ?? null,
  };
}

export function transformBiNumberFieldMetaToGq(
  fieldMeta: BiNumberFieldMeta
): GQBiFieldNumberMeta {
  return {
    __typename: 'BiFieldNumberMeta',
    ...transformFieldMetaToBaseGqFieldMeta(fieldMeta),
    precision: fieldMeta.precision ?? null,
    disableCommas: fieldMeta.disableCommas ?? null,
    includeUnit: fieldMeta.includeUnit ?? null,
    shouldFormatToPercent: fieldMeta.shouldFormatToPercent ?? null,
    // If we find we want the original units-library compatible unit in the frontend
    // we should change this to preserve the unit here and add a new gql field for
    // unitUserFacing. Then we'd need to do this coalescing in the frontend display code.
    unit: fieldMeta.unitUserFacing ?? fieldMeta.unit ?? null,
    unitDimension: fieldMeta.unitDimension ?? null,
    dependentDimensions: fieldMeta.dependentDimensions ?? null,
    aggregatedUnitDimension: fieldMeta.aggregatedUnitDimension ?? null,
    aggregateType: fieldMeta.aggregateType ?? null,
  };
}

export const biMeasureMetaSchema = z.object({
  supportedDimensions: z.array(z.string()),
  fieldMeta: biMeasureFieldMetaSchema,
});
export type BiMeasureMeta = z.infer<typeof biMeasureMetaSchema>;

export const biQueryFilterSchema = z.object({
  dimension: z.string(),
  // TODO: technically this is not a mixed type but iteration of
  // the union on the actual array makes typing very annoying
  value: z.array(z.union([z.string(), z.boolean()]).nullable()),
  operator: z.enum(['In', 'NotIn', 'Empty', 'NotEmpty']),
});
export const biArrayQueryFilterSchema = z.array(biQueryFilterSchema);

export type BiQueryFilter = z.TypeOf<typeof biQueryFilterSchema>;

const dimensionComputedTypeSchema = z.enum([
  // this indicates that we will take the numerator value either dimensions or filters and intersect it's dimension values
  // with the supportedDimensions of the normalizingQuery measure
  // for dimensions this is obvious
  // for filters it means removing any filters whos dimension isn't the supportedDimensions of the normalizingQuery measure
  'NumeratorIntersection',
]);

export const biDynamicNormalizedMeasureSchema = z.object({
  baseMeasure: z.string(),
  normalizingQuery: z
    .intersection(
      z.intersection(
        z.object({
          measure: z.string(),
        }),
        z.union([
          z.object({
            dimensions: z.array(z.string()),
          }),
          z.object({
            computedDimensionType: dimensionComputedTypeSchema,
          }),
        ])
      ),
      z.union([
        z.object({
          filters: biQueryFilterSchema.array(),
        }),
        z.object({
          computedFiltersType: dimensionComputedTypeSchema,
        }),
      ])
    )
    .nullish(),
});

export const biArrayDynamicNormalizedMeasureSchema = z.array(
  biDynamicNormalizedMeasureSchema
);

export type BiDynamicNormalizedMeasure = z.TypeOf<
  typeof biDynamicNormalizedMeasureSchema
>;

function computeDimensionsForNormalizedMeasure(
  normalizingQuery: NonNullable<BiDynamicNormalizedMeasure['normalizingQuery']>,
  query: BiQueryComponents,
  // TODO: probably should actually intersect the numerator?
  // for now this is only used when the measure is identical so it is functionally equivalent
  biMetadata: BiMetadata
) {
  if ('dimensions' in normalizingQuery) {
    return normalizingQuery.dimensions;
  }
  const denominatorMeta = findMeasureMeta(normalizingQuery.measure, biMetadata);

  const { computedDimensionType } = normalizingQuery;
  switch (computedDimensionType) {
    case 'NumeratorIntersection':
      const commonSelectedDimensions = query.dimensions.filter((dimension) =>
        denominatorMeta.supportedDimensions.includes(dimension)
      );
      return commonSelectedDimensions;
    default:
      assertNever(computedDimensionType);
  }
}

function computeFiltersForNormalizedMeasure(
  normalizingQuery: NonNullable<BiDynamicNormalizedMeasure['normalizingQuery']>,
  query: BiQueryComponents,

  biMetadata: BiMetadata
) {
  if ('filters' in normalizingQuery) {
    return normalizingQuery.filters;
  }

  const denominatorMeta = findMeasureMeta(normalizingQuery.measure, biMetadata);
  const { computedFiltersType } = normalizingQuery;
  switch (computedFiltersType) {
    case 'NumeratorIntersection':
      // TODO: is this right? we are intersect the numerator filters with the supportedDimensions of the denom measures
      // for now this is only used when the measure is identical so it is functionally equivalent either way
      // but worth being aware of this if we allow  users to specify the option for differing measures
      return (query.filters ?? []).filter(({ dimension }) =>
        denominatorMeta.supportedDimensions.includes(dimension)
      );
    default:
      assertNever(computedFiltersType);
  }
}

export function transformRawBiQueryComponentsToGqlAndHydrate(
  query: Omit<BiQueryComponents, 'normalizedMeasures'> & {
    normalizedMeasures: Array<BiDynamicNormalizedMeasure>;
  },
  biMetadata: BiMetadata
): Array<GQBiQueryComponentsInput> {
  const normalizedMeasuresWithMeta = query.normalizedMeasures.map((m) =>
    addMetaToDynamicNormalizedMeasure(m, biMetadata)
  );
  return transformBiQueryComponentsToGqlAndHydrate(
    {
      ...query,
      normalizedMeasures: normalizedMeasuresWithMeta,
    },
    biMetadata
  );
}

export function transformBiQueryComponentsToGqlAndHydrate(
  query: BiQueryComponents,
  biMetadata: BiMetadata
): Array<GQBiQueryComponentsInput> {
  const { timeIntervals, ...restQuery } = query;
  return timeIntervals.map((timeInterval) => ({
    ...restQuery,
    timeInterval,
    normalizedMeasures: query.normalizedMeasures.map((normalizedMeasure) => {
      return {
        ...omit(normalizedMeasure, 'measureMeta'),
        fieldId: normalizedMeasure.measureMeta.fieldMeta.fieldId,
        normalizingQuery: normalizedMeasure.normalizingQuery
          ? {
              measure: normalizedMeasure.normalizingQuery.measure,
              dimensions: computeDimensionsForNormalizedMeasure(
                normalizedMeasure.normalizingQuery,
                query,
                biMetadata
              ),
              filters: computeFiltersForNormalizedMeasure(
                normalizedMeasure.normalizingQuery,
                query,
                biMetadata
              ),
            }
          : undefined,
      };
    }),
  }));
}

export function getProperlyTypedMetadata(
  gqlMetadata: GQBiQueryMetadataResult
): BiMetadata {
  return {
    ...gqlMetadata,
    timeInterval: gqlMetadata.timeInterval ?? undefined,
    dimensions: gqlMetadata.dimensions.map((dimension) => ({
      ...dimension,
      fieldMeta: transformGqlFieldMetaToStandardMeta(dimension.fieldMeta),
    })),
    measures: gqlMetadata.measures.map((measure) => ({
      ...measure,
      fieldMeta: transformGqlFieldMetaToStandardMeta(measure.fieldMeta),
    })),
  };
}

export type BiDynamicNormalizedMeasureWithMeta = BiDynamicNormalizedMeasure & {
  measureMeta: BiMeasureMeta;
};

export interface BiNormalizedMeasure extends BiDynamicNormalizedMeasure {
  measureMeta: BiMeasureMeta;
}

export const biArrayQueryDimensionSchema = z.array(z.string());
export type BiDimensions = z.infer<typeof biArrayQueryDimensionSchema>;

export const biQueryOrderSchema = z.object({
  dimensionOrMeasure: z.string(),
  direction: z.nativeEnum(GQBiQueryOrderDirection),
});

export const biSavedViewQuerySnapshotContext = z.object({
  mode: z.literal(GQBiQueryMode.Snapshot),
  footprintKind: z.string(),
});

export const biSavedViewQueryContextSchema = z
  .discriminatedUnion('mode', [biSavedViewQuerySnapshotContext])
  .nullable();

export type BiSavedViewQueryContextType = z.TypeOf<
  typeof biSavedViewQueryContextSchema
>;

export const biSavedViewSchema = z.object({
  id: z.string(),
  stableId: z.string(),
  orgId: z.string(),
  ownerId: z.string(),
  editorId: z.string(),
  viewVisibility: z.nativeEnum(GQBiSavedViewVisibility),
  editVisibility: z.nativeEnum(GQBiSavedViewVisibility),
  jsonSchemaVersion: z.string(),
  name: z.string(),
  filters: biArrayQueryFilterSchema,
  dimensions: biArrayQueryDimensionSchema,
  normalizedMeasures: biArrayDynamicNormalizedMeasureSchema,
  timeIntervals: z.array(ymIntervalSchema),
  order: biQueryOrderSchema.nullable(),
  chartKind: z.nativeEnum(GQBiChartKind),
  lastVersionId: z.string().nullable(),
  searchTerm: z.string().nullable(),
  queryContext: biSavedViewQueryContextSchema,
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});
export type BiSavedViewType = z.TypeOf<typeof biSavedViewSchema>;

// Note: this is a subset of the full type for FE purposes only,
// excluding certain fields that we don't need on the client side
// and hydrating the normalized measures with their meta
export const biSavedViewPartialSchema = biSavedViewSchema
  .omit({
    // use stableId instead
    id: true,
    orgId: true,
    editorId: true,
    jsonSchemaVersion: true,
    lastVersionId: true,
    createdAt: true,
    updatedAt: true,
  })
  .merge(
    z.object({
      isDraft: z.boolean().optional().default(false),
      intervalType: z.nativeEnum(SavedViewTemplateIntervalType).optional(),
    })
  );

export type BiSavedViewPartialType = z.TypeOf<typeof biSavedViewPartialSchema>;

export type CustomReportInfo = {
  id: string;
  name: string;
  configId: string;
};

export function toGqlNormalizingQuery(
  normalizingQuery: BiDynamicNormalizedMeasure['normalizingQuery'] | null
): GQBiSavedView['normalizedMeasures'][number]['normalizingQuery'] | null {
  if (!normalizingQuery) {
    return null;
  }
  return {
    measure: normalizingQuery.measure,
    dimensions:
      'dimensions' in normalizingQuery ? normalizingQuery.dimensions : null,
    filters: 'filters' in normalizingQuery ? normalizingQuery.filters : null,
    computedDimensionType:
      'computedDimensionType' in normalizingQuery
        ? normalizingQuery.computedDimensionType
        : null,
    computedFiltersType:
      'computedFiltersType' in normalizingQuery
        ? normalizingQuery.computedFiltersType
        : null,
  };
}

export function gqlQueryContextToDbType(
  queryContext?: GQBiSavedViewQueryContextInput | null
): BiSavedViewType['queryContext'] {
  return queryContext?.snapshot
    ? {
        // Note: we only support snapshot queries in saved views for now
        mode: GQBiQueryMode.Snapshot,
        footprintKind: queryContext.snapshot.footprintKind,
      }
    : null;
}

export function dbQueryContextToGqlType(
  queryContext?: BiSavedViewType['queryContext']
): GQBiSavedViewQueryContextInput | null {
  return queryContext?.footprintKind
    ? {
        // Note: we only support snapshot queries in saved views for now
        mode: GQBiQueryMode.Snapshot,
        snapshot: {
          footprintKind: queryContext.footprintKind,
        },
      }
    : null;
}

export interface BiUnhydratedQueryComponents {
  filters: Array<BiQueryFilter>;
  dimensions: BiDimensions;
  normalizedMeasures: Array<BiDynamicNormalizedMeasure>;
  timeIntervals: Array<YMInterval>;
}

export interface BiQueryComponents
  extends Omit<BiUnhydratedQueryComponents, 'normalizedMeasures'> {
  normalizedMeasures: Array<BiNormalizedMeasure>;
}

export const biMetadataSchema = z.object({
  timeInterval: ymIntervalSchema.optional(),
  measures: biMeasureMetaSchema.array(),
  dimensions: z.array(z.object({ fieldMeta: biDimensionFieldMetaSchema })),
});
export type BiMetadata = z.infer<typeof biMetadataSchema>;

export interface BiSupportedMeasure {
  measure: string;
  supportedDenominators: Array<string>;
}

/**
 * Creates keys for use with hash and local storage atoms
 */
export const BiQueryComponentHashKey = {
  filters: biLocalStorageKey('filters'),
  dimensions: biLocalStorageKey('dimensions'),
  intervals: biLocalStorageKey('intervals'),
  measures: biLocalStorageKey('measures'),
  view: biLocalStorageKey('view'),
  footprintKind: biLocalStorageKey('footprintKind'),
  showCharts: biLocalStorageKey('showCharts'),
  chartKind: biLocalStorageKey('chartKind'),
  showTable: biLocalStorageKey('showTable'),
  tableKind: biLocalStorageKey('tableKind'),
  timeSeriesDimension: biLocalStorageKey('timeSeriesDimension'),
} as const;

/**
 * Creates namespaced keys for use with local storage atoms
 */
function biLocalStorageKey(prefix: string): string {
  return `bi:${prefix}`;
}
